import { GradeBadge } from "./DailyMetricsCard2";
import React, { useState, useEffect } from "react";
import {
  LineChart,
  Bar,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
} from "recharts";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PersonIcon from "@mui/icons-material/Person";
import {
  fillLaborSummaryQuarterHours,
  get_t_str,
} from "../../../../helpers/helpers";

export const SERVICE_LABOR_COLOR = "#cbd5e1";
export const SERVICE_AREA_FILL_OPACITY = 0.46;
export const SUPPORT_AREA_FILL = "#86efac";
export const SUPPORT_AREA_FILL_OPACITY = 0.24;
export const PRIMARY_LABOR_COLOR = "#6d28d9";

const VisualCustomLineChartForChartCard2 = (props) => {
  const [data, setData] = useState(props.data);
  const [hiddenLines, setHiddenLines] = useState([]);

  useEffect(() => {
    setData(props.data); // Update data state
  }, [props.data]);

  const handleDataChange = (index, field, newValue) => {
    if (newValue >= 0) {
      setData((prevData) => {
        const newData = [...prevData];
        newData[index][field] = newValue;
        newData[index].employeesOptimal = Object.keys(newData[index])
          .filter((key) => key.endsWith(` ${props.label1} Labor`))
          .reduce((sum, key) => sum + newData[index][key], 0); // Update total employeesOptimal
        return newData;
      });
    }
  };

  const handleInputChange = (index, field, event) => {
    const newValue = parseInt(event.target.value, 10);
    handleDataChange(index, field, newValue);
  };

  const handleCheckboxChange = (dataKey) => {
    setHiddenLines((prevHiddenLines) => {
      if (prevHiddenLines.includes(dataKey)) {
        return prevHiddenLines.filter((key) => key !== dataKey);
      } else {
        return [...prevHiddenLines, dataKey];
      }
    });
  };

  const getCheckboxColor = (name) => {
    if (name.includes(`${props.label1} Service`)) {
      return SERVICE_LABOR_COLOR;
    } else if (name.includes(props.label2)) {
      return PRIMARY_LABOR_COLOR;
    } else {
      return SUPPORT_AREA_FILL;
    }
  };

  const roleTypes = props.roleTypes;

  // console.log("INDEX", props.index);

  const showCheckBoxOption = (name, label) => {
    return (
      <div className="flex items-center mt-1">
        <input
          type="checkbox"
          checked={!hiddenLines.includes(name)}
          onChange={() => handleCheckboxChange(name)}
          style={{
            appearance: "none",
            WebkitAppearance: "none",
            width: props.isSmall ? "8px" : "12px",
            height: props.isSmall ? "8px" : "12px",
            borderRadius: "50px",
            border: `2px solid ${getCheckboxColor(name)}`,
            outline: "none",
            marginRight: "5px",
            cursor: "pointer",
            backgroundColor: !hiddenLines.includes(name)
              ? getCheckboxColor(name)
              : "transparent",
          }}
        />
        <label htmlFor={name} className={`ml-1 ${props.isSmall && "text-xs"}`}>
          {label}
        </label>
      </div>
    );
  };

  const showDataRow = (name, bold) => {
    return (
      <tr key={name}>
        <td
          className={
            "border px-5 py-4 text-xs whitespace-nowrap bg-gray-100 " + bold
          }
          style={{
            position: "sticky",
            left: 0,
            zIndex: 1,
            minWidth: "150px",
          }}
        >
          {name}
        </td>
        {data.map((entry, index) => (
          <td key={index} className="border">
            <div
              className={
                "h-full w-full flex items-center justify-center text-sm m-auto " +
                bold
              }
            >
              <p>{entry[name] ? entry[name] : 0}</p>
            </div>
          </td>
        ))}
      </tr>
    );
  };

  const showLine = (name) => {
    return (
      !hiddenLines.includes(name) && (
        <Line
          type="stepAfter"
          dataKey={name}
          stroke={getCheckboxColor(name)}
          strokeWidth={1}
          // dot={{ onMouseDown: (event, payload) => event.stopPropagation() }}
          isAnimationActive={true}
          dot={false}
        />
      )
    );
  };

  const showStackedArea = (name) => {
    const isSupportArea = name === `${props.label1} Support Total`;
    const isServiceArea = name === `${props.label1} Service Total`;

    return (
      !hiddenLines.includes(name) && (
        <Area
          type="stepAfter"
          dataKey={name}
          //stroke={getCheckboxColor(name)}
          // strokeWidth={2}
          // dot={{ onMouseDown: (event, payload) => event.stopPropagation() }}
          // isAnimationActive={true}
          fill={getCheckboxColor(name)}
          fillOpacity={
            isSupportArea
              ? SUPPORT_AREA_FILL_OPACITY
              : isServiceArea
              ? SERVICE_AREA_FILL_OPACITY
              : undefined
          }
          stroke={isSupportArea ? "transparent" : getCheckboxColor(name)}
          stackId="1"
        />
      )
    );
  };

  const showArea = (name) => {
    return (
      !hiddenLines.includes(name) && (
        <Area
          type="stepAfter"
          dataKey={name}
          //stroke={getCheckboxColor(name)}
          //strokeWidth={2}
          //dot={{ onMouseDown: (event, payload) => event.stopPropagation() }}
          isAnimationActive={false}
          fill={getCheckboxColor(name)}
        />
      )
    );
  };

  const CustomTooltip = ({ active, payload, label, coordinate }) => {
    const serviceTotal = payload?.[0]?.value;
    const supportTotal = payload?.[1]?.value;
    const scheduledTotal = payload?.[2]?.value;
    const forecastedTotal = supportTotal + serviceTotal;
    const difference = scheduledTotal - forecastedTotal;

    if (active && payload && payload.length) {
      // Calculate tooltip positioning
      let left = coordinate ? coordinate.x : 0;
      let transform = "translateX(-50%)";

      // Get the position of the tooltip from the coordinate prop
      const tooltipStyle = {
        position: "absolute",
        left: `${left}px`,
        top: "-200px",
        transform: transform,
        pointerEvents: "none",
        zIndex: 9999,
      };

      return (
        <div
          className="bg-white border border-gray-300 w-60 px-6 py-3.5 rounded-xl shadow-xl"
          style={tooltipStyle}
        >
          <div className="w-full flex items-center justify-between mb-3">
            <p className="text-slate-900 text-xs">{`${label}`}</p>
            <p className="text-slate-900 text-xs">Total</p>
          </div>
          {payload.map((entry, index) => {
            const words = entry.name.split(" ");
            const modifiedName = words.slice(0, -1).join(" ");
            const markerColor = getCheckboxColor(entry.name);

            return (
              <div
                key={index}
                className={`w-full flex items-center justify-between py-1 ${
                  index !== 0 ? "border-t border-slate-200" : ""
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-2 h-2 rounded-full`}
                    style={{ backgroundColor: markerColor }}
                  ></div>
                  <div className="text-slate-500 text-xs">{modifiedName}</div>
                </div>
                <div className="text-slate-900 text-xs">{entry.value}</div>
              </div>
            );
          })}
          {difference === 0 ? (
            <div className="mt-1 flex flex-col items-center justify-center text-emerald-600">
              <div>
                <CheckIcon style={{ fontSize: "22px" }} />
              </div>
              <div className="text-xs">Perfect</div>
            </div>
          ) : difference < 0 ? (
            <div className="mt-1 flex flex-col items-center justify-center text-blue-600">
              <div className="flex items-center space-x-1">
                <div className="font-bold text-base">
                  {Math.abs(difference)}
                </div>
                <div>
                  <PersonIcon
                    style={{ fontSize: "18px", marginBottom: "3px" }}
                  />
                </div>
              </div>
              <div className="text-xs">Understaffed</div>
            </div>
          ) : (
            <div className="mt-1 flex flex-col items-center justify-center text-rose-600">
              <div className="flex items-center space-x-1">
                <div className="font-bold text-base">
                  {Math.abs(difference)}
                </div>
                <div>
                  <PersonIcon
                    style={{ fontSize: "18px", marginBottom: "3px" }}
                  />
                </div>
              </div>
              <div className="text-xs">Overstaffed</div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const scheduledHours = props.agg_column(
    props.actual_schedule_labor_day,
    "hours"
  );
  const forecastedHours = props.agg_column(props.forecasted_labor_day, "hours");
  const actualHours = props.agg_column(
    props.actual_used_labor_vs_earned_day,
    "hours"
  );
  const earnedHours = props.agg_column(
    props.calculated_earned_labor_day,
    "hours"
  );
  const hasChartRange =
    Number.isFinite(props.start_h) &&
    Number.isFinite(props.end_h) &&
    props.end_h > props.start_h;
  const chartData = hasChartRange
    ? fillLaborSummaryQuarterHours(
        data,
        get_t_str,
        props.label1,
        props.label2,
        props.start_h,
        props.end_h
      )
    : data;

  const usedHours = props.earned_toggle ? actualHours : scheduledHours;
  const targetHours = props.earned_toggle ? earnedHours : forecastedHours;
  const over = targetHours !== 0 && usedHours > targetHours;
  const dayLabel = props.date_name?.split(",")[0] || props.date_name;

  // Explicit even ticks so the Y axis labels and the grid lines always agree.
  // Shared floor of 8 so every day in the row uses the same scale; a day only
  // grows the axis if its staffing actually exceeds it.
  const yMax = Math.max(
    8,
    ...chartData.map((row) =>
      Math.max(
        (row[`${props.label1} Service Total`] || 0) +
          (row[`${props.label1} Support Total`] || 0),
        row[`${props.label2} Labor Total`] || 0
      )
    )
  );
  const yTop = Math.ceil(yMax / 2) * 2;
  const yTicks = [];
  for (let v = 0; v <= yTop; v += 2) yTicks.push(v);

  return (
    <div className={`relative w-full ${props.hideHeader ? "" : "pt-1"}`}>
      {!props.hideHeader && (
      <div className="w-full flex items-center justify-between gap-2 leading-none">
        <span className="text-xs font-bold text-slate-900">{dayLabel}</span>
        <div className="flex items-center gap-2">
          <p className="text-xxs tabular-nums">
            <span
              className={`font-semibold ${
                over ? "text-rose-700" : "text-slate-700"
              }`}
            >
              {usedHours.toFixed(1)}
            </span>
            <span className="text-slate-500"> / {targetHours.toFixed(1)}</span>
          </p>
          {props.grade && (
            <GradeBadge color={props.grade.color} label={props.grade.letter} />
          )}
        </div>
      </div>
      )}
      <div>
        {
          <ResponsiveContainer
            width={"100%"}
            height={props.height}
            padding={0}
          >
            <ComposedChart
              data={chartData}
              margin={{ top: 12, right: 2, bottom: 0, left: -6 }}
            >
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12 }}
                tickLine={false}
                interval="preserveEnd"
                minTickGap={20}
                hide={true}
              />
              <YAxis
                width={22}
                domain={[0, yTop]}
                ticks={yTicks}
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              {yTicks.map((v) => (
                <ReferenceLine
                  key={v}
                  y={v}
                  stroke="#e2e8f0"
                  strokeWidth={1}
                  ifOverflow="visible"
                />
              ))}

              {showStackedArea(`${props.label1} Service Total`)}
              {showStackedArea(`${props.label1} Support Total`)}
              {showLine(`${props.label2} Labor Total`)}
              <Tooltip content={<CustomTooltip />} />
            </ComposedChart>
          </ResponsiveContainer>
        }
      </div>
    </div>
  );
};

export default VisualCustomLineChartForChartCard2;
